import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { FiClock, FiLogIn, FiLogOut, FiCoffee, FiCalendar, FiUsers, FiUser, FiEdit2 } from 'react-icons/fi';
import './HR.css';

const STATUS_COLOR = {
  present: '#48bb78', absent: '#f56565', late: '#ed8936',
  'half-day': '#ecc94b', holiday: '#667eea', weekend: '#a0aec0',
  wfh: '#06b6d4', 'not-checked-in': '#718096',
};

const fmtTime = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtElapsed = (ms) => {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2,'0');
  const ss = String(s).padStart(2,'0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};
// Convert decimal hours (e.g. 7.87) → "7h 52m"
const fmtHours = (dec) => {
  if (!dec && dec !== 0) return '—';
  const v = parseFloat(dec) || 0;
  const h = Math.floor(v);
  const m = Math.round((v - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};
const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab,         setTab]         = useState(isAdmin ? 'all' : 'my');
  const [todaySelf,   setTodaySelf]   = useState(null);
  const [allToday,    setAllToday]    = useState([]);
  const [report,      setReport]      = useState([]);
  const [summary,     setSummary]     = useState({});
  const [employees,   setEmployees]   = useState([]);
  const [selEmp,      setSelEmp]      = useState('');
  const [month,       setMonth]       = useState(new Date().getMonth() + 1);
  const [year,        setYear]        = useState(new Date().getFullYear());
  const [clock,       setClock]       = useState(new Date());
  const [loading,     setLoading]     = useState(false);
  const [manualModal, setManualModal] = useState(false);
  const [manualForm,  setManualForm]  = useState({
    employeeId: '', date: new Date().toISOString().split('T')[0],
    status: 'present', checkIn: '', checkOut: '',
  });

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users?limit=200')
      .then(r => setEmployees((r.data.data || r.data.users || []).filter(u => u.role !== 'client')))
      .catch(() => {});
  }, [isAdmin]);

  const loadTodaySelf = useCallback(async () => {
    try { const r = await api.get('/attendance/today'); setTodaySelf(r.data.data); }
    catch (e) { console.error(e); }
  }, []);

  const loadAllToday = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await api.get('/attendance/all-today'); setAllToday(r.data.data || []); }
    catch (e) { console.error(e); }
  }, [isAdmin]);

  const loadReport = useCallback(async (empId) => {
    try {
      const eId = empId !== undefined ? empId : selEmp;
      const params = `month=${month}&year=${year}${isAdmin && eId ? `&employeeId=${eId}` : ''}`;
      const r = await api.get(`/attendance/report?${params}`);
      setReport(r.data.data || []);
      setSummary(r.data.summary || {});
    } catch (e) { console.error(e); }
  }, [month, year, selEmp, isAdmin]);

  useEffect(() => { loadTodaySelf(); }, [loadTodaySelf]);

  useEffect(() => {
    if (tab === 'all')    loadAllToday();
    if (tab === 'my')     loadReport('');
    if (tab === 'report') { /* triggered by button */ }
  }, [tab]); // eslint-disable-line

  const act = async (fn, msg) => {
    setLoading(true);
    try { await fn(); toast.success(msg); loadTodaySelf(); if (tab === 'all') loadAllToday(); }
    catch (e) { toast.error(e.response?.data?.message || 'Error'); }
    setLoading(false);
  };

  const handleManual = async (e) => {
    e.preventDefault();
    try {
      await api.post('/attendance/manual', manualForm);
      toast.success('Attendance marked');
      setManualModal(false);
      loadAllToday();
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); }
  };

  /* ─────────────────────────────────── RENDER ─────────────────────── */
  return (
    <div className="hr-page">
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:10 }}>
        <h1 style={{ margin:0 }}>Attendance</h1>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {isAdmin && (
            <>
              <button style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, background: tab==='all' ? '#667eea' : '#edf2f7', color: tab==='all' ? '#fff' : '#4a5568' }}
                onClick={() => setTab('all')}>
                <FiUsers style={{ marginRight:4 }} />All Employees
              </button>
              <button style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, background: tab==='report' ? '#667eea' : '#edf2f7', color: tab==='report' ? '#fff' : '#4a5568' }}
                onClick={() => setTab('report')}>
                <FiCalendar style={{ marginRight:4 }} />Reports
              </button>
            </>
          )}
          <button style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, background: tab==='my' ? '#667eea' : '#edf2f7', color: tab==='my' ? '#fff' : '#4a5568' }}
            onClick={() => { setTab('my'); loadReport(''); }}>
            <FiUser style={{ marginRight:4 }} />My Attendance
          </button>
          {isAdmin && (
            <button style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, background:'#48bb78', color:'#fff' }}
              onClick={() => setManualModal(true)}>
              <FiEdit2 style={{ marginRight:4 }} />Manual Mark
            </button>
          )}
        </div>
      </div>

      {/* ── TAB: ALL EMPLOYEES TODAY ── */}
      {tab === 'all' && isAdmin && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h2 style={{ margin:0 }}>Today's Attendance — All Employees</h2>
            <button onClick={loadAllToday} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13 }}>
              ↻ Refresh
            </button>
          </div>

          {/* Quick stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
            {[
              { label:'Total Logged',  val: allToday.length,                                         color:'#667eea' },
              { label:'Present',       val: allToday.filter(r=>r.status==='present').length,         color:'#48bb78' },
              { label:'Late',          val: allToday.filter(r=>r.status==='late').length,            color:'#ed8936' },
              { label:'WFH',           val: allToday.filter(r=>r.status==='wfh').length,             color:'#06b6d4' },
              { label:'Half Day',      val: allToday.filter(r=>r.status==='half-day').length,        color:'#ecc94b' },
              { label:'On Break',      val: allToday.filter(r=>r.activeBreak).length,                color:'#f687b3' },
              { label:'Checked Out',   val: allToday.filter(r=>r.checkOut).length,                   color:'#a0aec0' },
            ].map(s => (
              <div key={s.label} style={{ background:'#f7fafc', borderRadius:10, padding:'12px 16px', textAlign:'center', border:`2px solid ${s.color}33` }}>
                <div style={{ fontSize:26, fontWeight:700, color:s.color }}>{s.val}</div>
                <div style={{ fontSize:11, color:'#718096', marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="table-wrap">
            <table className="hr-table">
              <thead>
                <tr><th>Employee</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Work Hrs</th><th>Breaks</th><th>Overtime</th></tr>
              </thead>
              <tbody>
                {allToday.length === 0 && (
                  <tr><td colSpan="7" className="empty">No check-ins recorded today yet.</td></tr>
                )}
                {allToday.map(r => {
                  const breaks = Array.isArray(r.breaks) ? r.breaks : (r.breaks ? JSON.parse(r.breaks) : []);
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight:600 }}>{r.employee?.name}</div>
                        <div style={{ fontSize:11, color:'#a0aec0' }}>{r.employee?.position || r.employee?.email}</div>
                      </td>
                      <td>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background: STATUS_COLOR[r.status]||'#718096', display:'inline-block' }} />
                          {r.activeBreak ? '🟡 On Break' : r.status}
                        </span>
                      </td>
                      <td>{fmtTime(r.checkIn)}</td>
                      <td>{fmtTime(r.checkOut)}</td>
                      <td>{fmtHours(r.workHours)}</td>
                      <td>
                        {breaks.length === 0 ? (
                          <span style={{ color:'#a0aec0' }}>—</span>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            {breaks.map((b, i) => (
                              <div key={i} style={{
                                display:'flex', alignItems:'center', gap:4,
                                background: b.endTime ? '#f0fff4' : '#fffbeb',
                                border: `1px solid ${b.endTime ? '#c6f6d5' : '#fef3c7'}`,
                                borderRadius:6, padding:'3px 8px', fontSize:11,
                              }}>
                                <span style={{ color:'#f59e0b', fontWeight:700 }}>☕</span>
                                <span style={{ color:'#2d3748', fontWeight:600 }}>
                                  {fmtTime(b.startTime)}
                                </span>
                                <span style={{ color:'#a0aec0' }}>→</span>
                                <span style={{ color: b.endTime ? '#276749' : '#b7791f', fontWeight:600 }}>
                                  {b.endTime ? fmtTime(b.endTime) : 'ongoing'}
                                </span>
                                {b.duration && (
                                  <span style={{ color:'#718096', marginLeft:2 }}>({b.duration}m)</span>
                                )}
                              </div>
                            ))}
                            <div style={{ fontSize:10, color:'#718096', fontWeight:600, marginTop:1 }}>
                              Total: {r.breakHours || 0} min
                            </div>
                          </div>
                        )}
                      </td>
                      <td>{fmtHours(r.overtime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB: REPORTS (admin, filter by employee & month) ── */}
      {tab === 'report' && isAdmin && (
        <div>
          <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
            <select style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, minWidth:200 }}
              value={selEmp} onChange={e => setSelEmp(e.target.value)}>
              <option value="">— Select Employee —</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <select style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }}
              value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }}
              value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => loadReport()} style={{ padding:'8px 18px', borderRadius:8, background:'#667eea', color:'#fff', border:'none', cursor:'pointer', fontWeight:600, fontSize:13 }}>
              Load Report
            </button>
          </div>

          {!selEmp ? (
            <div className="card" style={{ textAlign:'center', padding:48, color:'#a0aec0' }}>
              Select an employee above to view their attendance report.
            </div>
          ) : (
            <>
              <div className="card summary-card" style={{ marginBottom:20 }}>
                <h2><FiCalendar /> Monthly Summary — {employees.find(e => String(e.id) === String(selEmp))?.name} ({MONTHS[month-1]} {year})</h2>
                <div className="summary-grid">
                  <div className="summary-item"><span>{summary.present||0}</span><p>Present</p></div>
                  <div className="summary-item"><span>{summary.absent||0}</span><p>Absent</p></div>
                  <div className="summary-item"><span>{summary.late||0}</span><p>Late</p></div>
                  <div className="summary-item"><span>{summary.halfDay||0}</span><p>Half Day</p></div>
                  <div className="summary-item"><span>{summary.totalWorkHours||0}</span><p>Work Hrs</p></div>
                  <div className="summary-item"><span>{summary.totalOvertime||0}</span><p>Overtime</p></div>
                </div>
              </div>
              <div className="card">
                <div className="table-wrap">
                  <table className="hr-table">
                    <thead>
                      <tr><th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Work Hrs</th><th>Break</th><th>Overtime</th></tr>
                    </thead>
                    <tbody>
                      {report.map(r => (
                        <tr key={r._id}>
                          <td>{fmtDate(r.date)}</td>
                          <td><span className="status-dot" style={{ background: STATUS_COLOR[r.status] }} />{r.status}</td>
                          <td>{fmtTime(r.checkIn)}</td>
                          <td>{fmtTime(r.checkOut)}</td>
                          <td>{fmtHours(r.workHours)}</td>
                          <td>{r.breakHours ? `${r.breakHours} min` : '—'}</td>
                          <td>{fmtHours(r.overtime)}</td>
                        </tr>
                      ))}
                      {report.length === 0 && <tr><td colSpan="7" className="empty">No records found for this employee/period.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TAB: MY ATTENDANCE ── */}
      {tab === 'my' && (
        <>
          <div className="hr-top-grid">
            <div className="card clock-card">
              <div className="live-clock">{clock.toLocaleTimeString()}</div>
              <div className="live-date">{clock.toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</div>
              {(() => {
                const activeBreakStart = todaySelf?.activeBreak
                  ? new Date((todaySelf.breaks || []).filter(b => !b.endTime).slice(-1)[0]?.startTime || (todaySelf.breaks || []).slice(-1)[0]?.startTime)
                  : null;
                const breakElapsedMs = activeBreakStart ? clock - activeBreakStart : 0;

                return (
                  <>
                    <div className="today-status">
                      {todaySelf?.checkIn  && <div className="time-chip green"><FiLogIn />  IN  {fmtTime(todaySelf.checkIn)}</div>}
                      {todaySelf?.checkOut && <div className="time-chip red">  <FiLogOut /> OUT {fmtTime(todaySelf.checkOut)}</div>}
                      {todaySelf?.activeBreak && (
                        <div className="time-chip orange" style={{ fontWeight:800, letterSpacing:.5 }}>
                          <FiCoffee /> Break {fmtElapsed(breakElapsedMs)}
                        </div>
                      )}
                      {!todaySelf?.activeBreak && todaySelf?.breakHours > 0 && (
                        <div className="time-chip orange" style={{fontSize:11}}><FiCoffee /> Break: {todaySelf.breakHours} min</div>
                      )}
                      {todaySelf?.workHours > 0 && <div className="time-chip green" style={{fontSize:11}}>⏱ Worked: {fmtHours(todaySelf.workHours)}</div>}
                    </div>

                    {/* Break live timer card */}
                    {todaySelf?.activeBreak && (
                      <div style={{ margin:'12px 0', background:'linear-gradient(135deg,#fef3c7,#fde68a)', borderRadius:14, padding:'16px 20px',
                        border:'2px solid #f59e0b', textAlign:'center' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#92400e', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
                          ☕ Break in progress
                        </div>
                        <div style={{ fontSize:40, fontWeight:800, color:'#78350f', fontFamily:'monospace', letterSpacing:2, lineHeight:1 }}>
                          {fmtElapsed(breakElapsedMs)}
                        </div>
                        {activeBreakStart && (
                          <div style={{ fontSize:11, color:'#92400e', marginTop:6, opacity:.8 }}>
                            Started at {fmtTime(activeBreakStart)}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="action-btns">
                      {!todaySelf?.checkIn && (
                        <button onClick={() => act(() => api.post('/attendance/checkin'), 'Checked In!')} disabled={loading}
                          style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'#fff', opacity: loading ? 0.7 : 1 }}>
                          <FiLogIn /> Check In
                        </button>
                      )}
                      {todaySelf?.checkIn && !todaySelf?.checkOut && (
                        <>
                          {!todaySelf?.activeBreak
                            ? <button onClick={() => act(() => api.post('/attendance/break/start',{type:'short'}), 'Break started!')} disabled={loading}
                                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', opacity: loading ? 0.7 : 1 }}>
                                <FiCoffee /> Start Break
                              </button>
                            : <button onClick={() => act(() => api.post('/attendance/break/end'), 'Break ended!')} disabled={loading}
                                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, background:'var(--pm-gradient)', color:'#fff', opacity: loading ? 0.7 : 1 }}>
                                <FiCoffee /> End Break · {fmtElapsed(breakElapsedMs)}
                              </button>
                          }
                          <button onClick={() => act(() => api.post('/attendance/checkout'), 'Checked Out!')} disabled={loading}
                            style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'#fff', opacity: loading ? 0.7 : 1 }}>
                            <FiLogOut /> Check Out
                          </button>
                        </>
                      )}
                      {todaySelf?.checkOut && <div className="done-msg">✅ You have completed today's shift</div>}
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="card summary-card">
              <h2><FiCalendar /> Monthly Summary</h2>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <select style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13 }} value={month} onChange={e => { setMonth(parseInt(e.target.value)); }}>
                  {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
                <select style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13 }} value={year} onChange={e => { setYear(parseInt(e.target.value)); }}>
                  {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <button onClick={() => loadReport('')} style={{ padding:'5px 12px', borderRadius:6, background:'#667eea', color:'#fff', border:'none', cursor:'pointer', fontSize:13 }}>Go</button>
              </div>
              <div className="summary-grid">
                <div className="summary-item"><span>{summary.present||0}</span><p>Present</p></div>
                <div className="summary-item"><span>{summary.absent||0}</span><p>Absent</p></div>
                <div className="summary-item"><span>{summary.late||0}</span><p>Late</p></div>
                <div className="summary-item"><span>{summary.halfDay||0}</span><p>Half Day</p></div>
                <div className="summary-item"><span>{fmtHours(summary.totalWorkHours||0)}</span><p>Work Hrs</p></div>
                <div className="summary-item"><span>{fmtHours(summary.totalOvertime||0)}</span><p>Overtime</p></div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2><FiClock /> Attendance Log</h2>
            <div className="table-wrap">
              <table className="hr-table">
                <thead>
                  <tr><th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Work Hrs</th><th>Break</th><th>Overtime</th></tr>
                </thead>
                <tbody>
                  {report.map(r => (
                    <tr key={r._id}>
                      <td>{fmtDate(r.date)}</td>
                      <td><span className="status-dot" style={{ background: STATUS_COLOR[r.status] }} />{r.status}</td>
                      <td>{fmtTime(r.checkIn)}</td>
                      <td>{fmtTime(r.checkOut)}</td>
                      <td>{fmtHours(r.workHours)}</td>
                      <td>{r.breakHours ? `${r.breakHours} min` : '—'}</td>
                      <td>{fmtHours(r.overtime)}</td>
                    </tr>
                  ))}
                  {report.length === 0 && <tr><td colSpan="7" className="empty">No records for this month.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── MANUAL MARK MODAL ── */}
      {manualModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:'100%', maxWidth:420, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop:0, marginBottom:20 }}>Manual Attendance Mark</h3>
            <form onSubmit={handleManual}>
              {[
                { label:'Employee', type:'select', key:'employeeId', opts: employees.map(e=>({v:e.id,l:e.name})), required:true },
              ].map(f => (
                <div key={f.key} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:13, color:'#4a5568', display:'block', marginBottom:4 }}>{f.label} *</label>
                  <select style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}
                    value={manualForm[f.key]} onChange={e => setManualForm(p => ({...p, [f.key]: e.target.value}))} required>
                    <option value="">Select Employee</option>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:13, color:'#4a5568', display:'block', marginBottom:4 }}>Date *</label>
                <input type="date" style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}
                  value={manualForm.date} onChange={e => setManualForm(p => ({...p, date: e.target.value}))} required />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:13, color:'#4a5568', display:'block', marginBottom:4 }}>Status *</label>
                <select style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }}
                  value={manualForm.status} onChange={e => setManualForm(p => ({...p, status: e.target.value}))}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="half-day">Half Day</option>
                  <option value="wfh">WFH</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
                <div>
                  <label style={{ fontSize:13, color:'#4a5568', display:'block', marginBottom:4 }}>Check In</label>
                  <input type="time" style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}
                    value={manualForm.checkIn} onChange={e => setManualForm(p => ({...p, checkIn: e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:13, color:'#4a5568', display:'block', marginBottom:4 }}>Check Out</label>
                  <input type="time" style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, boxSizing:'border-box' }}
                    value={manualForm.checkOut} onChange={e => setManualForm(p => ({...p, checkOut: e.target.value}))} />
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" style={{ flex:1, padding:10, borderRadius:8, background:'#667eea', color:'#fff', border:'none', cursor:'pointer', fontWeight:600 }}>Save</button>
                <button type="button" style={{ flex:1, padding:10, borderRadius:8, background:'#edf2f7', color:'#4a5568', border:'none', cursor:'pointer', fontWeight:600 }}
                  onClick={() => setManualModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
